import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Компонент действий со строкой (копирование, удаление и т.д.)
 */
const RowActions = ({
  onCopy,
  onDelete,
  onMoveUp,
  onMoveDown,
  onInsertAbove,
  onInsertBelow,
  onHide,
  canMoveUp = true,
  canMoveDown = true,
  disableDelete = false,
  disableHide = false,
  disableMove = false,
  disableInsert = false,
  disableCopy = false,
  disabledTitle = '',
  isNew = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: Math.max(10, rect.right - 150),
      });
    }
  }, []);

  useEffect(() => {
    if (!showMenu) return;

    updateMenuPosition();

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    const handleScrollOrResize = () => updateMenuPosition();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showMenu, updateMenuPosition]);

  const handleToggleMenu = () => {
    updateMenuPosition();
    setShowMenu(!showMenu);
  };

  const moveTitle = disabledTitle || 'Переместить';

  return (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <button
          type="button"
          onClick={onMoveUp}
          disabled={disableMove || !canMoveUp}
          className="glass-btn glass-btn--xs"
          style={{ opacity: disableMove || !canMoveUp ? 0.4 : 1 }}
          title={disableMove ? disabledTitle : moveTitle}
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={disableMove || !canMoveDown}
          className="glass-btn glass-btn--xs"
          style={{ opacity: disableMove || !canMoveDown ? 0.4 : 1 }}
          title={disableMove ? disabledTitle : moveTitle}
        >
          ▼
        </button>
      </div>

      <div style={{ position: 'relative' }}>
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggleMenu}
          className="glass-btn glass-btn--xs glass-btn--icon-only"
          title="Действия"
        >
          ⋮
        </button>

        {showMenu &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="glass-dropdown"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                zIndex: 'var(--z-dropdown)',
                minWidth: '140px',
              }}
            >
              {onInsertAbove && (
                <ActionMenuItem
                  icon="⬆️"
                  label="Вставить выше"
                  disabled={disableInsert}
                  title={disableInsert ? disabledTitle : ''}
                  onClick={() => {
                    if (!disableInsert) {
                      onInsertAbove();
                      setShowMenu(false);
                    }
                  }}
                />
              )}

              {onInsertBelow && (
                <ActionMenuItem
                  icon="⬇️"
                  label="Вставить ниже"
                  disabled={disableInsert}
                  title={disableInsert ? disabledTitle : ''}
                  onClick={() => {
                    if (!disableInsert) {
                      onInsertBelow();
                      setShowMenu(false);
                    }
                  }}
                />
              )}

              {onHide && (
                <ActionMenuItem
                  icon="👁‍🗨"
                  label="Скрыть строку"
                  disabled={disableHide}
                  title={disableHide ? disabledTitle : ''}
                  onClick={() => {
                    if (!disableHide) {
                      onHide();
                      setShowMenu(false);
                    }
                  }}
                />
              )}

              {onCopy && (
                <ActionMenuItem
                  icon="📋"
                  label="Копировать"
                  disabled={disableCopy}
                  title={disableCopy ? disabledTitle : ''}
                  onClick={() => {
                    if (!disableCopy) {
                      onCopy();
                      setShowMenu(false);
                    }
                  }}
                />
              )}

              {onDelete && (
                <ActionMenuItem
                  icon="🗑️"
                  label="Удалить"
                  disabled={disableDelete}
                  title={disableDelete ? disabledTitle : ''}
                  onClick={() => {
                    if (!disableDelete) {
                      onDelete();
                      setShowMenu(false);
                    }
                  }}
                  danger
                />
              )}
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

const ActionMenuItem = ({ icon, label, onClick, danger = false, disabled = false, title = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`glass-dropdown__item${danger ? ' glass-dropdown__item--danger' : ''}`}
    style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

export default RowActions;
